<!--
  - SPDX-FileCopyrightText: 2019 Nextcloud GmbH and Nextcloud contributors
  - SPDX-License-Identifier: AGPL-3.0-or-later
-->

<template>
	<div
		:id="screenContainerId"
		class="screenContainer"
		@dblclick.capture="onDoubleClick">
		<video
			v-show="isLocalScreen || isRemoteScreen"
			ref="screen"
			:disablePictureInPicture="!isBig ? 'true' : 'false'"
			class="screen"
			:class="screenClass" />
		<NcEmptyContent
			v-if="isLocalScreen && showScreenPlaceholder"
			class="screen-placeholder"
			:name="t('spreed', 'You are sharing a screen')"
			:description="t('spreed', 'Sharing this window may cause a mirroring effect')"
			data-theme-dark>
			<template #icon>
				<IconMonitorScreenshot />
			</template>
			<template #action>
				<div class="screen-placeholder__actions">
					<NcButton variant="error" @click="stopLocalScreen">
						{{ t('spreed', 'Stop screensharing') }}
					</NcButton>
					<NcButton variant="tertiary" @click="showScreenPlaceholder = false">
						{{ t('spreed', 'Dismiss') }}
					</NcButton>
				</div>
			</template>
		</NcEmptyContent>
		<VideoBottomBar
			v-if="isBig"
			:token="token"
			:sharedData="sharedData"
			isBig
			isScreen
			:model="model"
			:showVideoOverlay="showVideoOverlay"
			:participantName="remoteParticipantName" />
	</div>
</template>

<script>
import { t } from '@nextcloud/l10n'
import Hex from 'crypto-js/enc-hex.js'
import SHA1 from 'crypto-js/sha1.js'
import panzoom from 'panzoom'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import NcButton from '@nextcloud/vue/components/NcButton'
import NcEmptyContent from '@nextcloud/vue/components/NcEmptyContent'
import IconMonitorScreenshot from 'vue-material-design-icons/MonitorScreenshot.vue'
import VideoBottomBar from './VideoBottomBar.vue'
import { useGuestNameStore } from '../../../stores/guestName.ts'
import attachMediaStream from '../../../utils/attachmediastream.js'

const ZOOM_MIN = 1
const ZOOM_FACTOR = 4
const ZOOM_MAX = 8

export default {

	name: 'ScreenShare',

	components: {
		IconMonitorScreenshot,
		NcButton,
		NcEmptyContent,
		VideoBottomBar,
	},

	props: {
		token: {
			type: String,
			required: true,
		},

		localMediaModel: {
			type: Object,
			default: null,
		},

		callParticipantModel: {
			type: Object,
			default: null,
		},

		sharedData: {
			type: Object,
			required: true,
		},

		isBig: {
			type: Boolean,
			default: false,
		},

		showVideoOverlay: {
			type: Boolean,
			default: true,
		},
	},

	setup(props) {
		const guestNameStore = useGuestNameStore()

		const screen = ref(null)
		const instance = ref(null)
		const instanceTransform = ref({ x: 0, y: 0, scale: 1 })
		const instanceGrabbing = ref(false)

		/* Flags and utilities to track, if we need to prevent mirroring effect */
		const showScreenPlaceholder = ref(false)
		const isLocalScreen = computed(() => props.localMediaModel && props.localMediaModel.attributes.localScreen)
		const isRemoteScreen = computed(() => props.callParticipantModel && props.callParticipantModel.attributes.screen)

		const screenClass = computed(() => {
			if (!props.isBig) {
				return ['screen--fill']
			} else {
				return [
					'screen--fit',
					instanceTransform.value.scale === 1
						? 'screen--magnify'
						: (instanceGrabbing.value ? 'screen--grabbing' : 'screen--grab'),
				]
			}
		})

		onMounted(() => {
			if (props.isBig) {
				instance.value = panzoom(screen.value, {
					minZoom: ZOOM_MIN,
					maxZoom: ZOOM_MAX,
					bounds: true,
					boundsPadding: 1,
				})
				instance.value.on('zoom', (instance) => {
					instanceTransform.value = instance.getTransform()
				})
				instance.value.on('panstart', () => {
					instanceGrabbing.value = true
				})
				instance.value.on('panend', () => {
					instanceGrabbing.value = false
				})
			}
		})
		onBeforeUnmount(() => {
			instance.value?.dispose()
		})

		/**
		 * Overriding method to handle double click event on screen share
		 *
		 * @param event Double click event
		 */
		function onDoubleClick(event) {
			if (!instance.value) {
				return
			}

			// panzoom library puts a listener on parent element
			event.preventDefault()
			event.stopPropagation()

			// Calculate the offset of the click event from the top left corner of screen container
			const screenContainerRect = event.currentTarget.getBoundingClientRect()
			const offsetX = event.clientX - screenContainerRect.left
			const offsetY = event.clientY - screenContainerRect.top

			if (instanceTransform.value.scale === 1) {
				// Zoom in the click point with specified zoom factor
				instance.value.smoothZoom(offsetX, offsetY, ZOOM_FACTOR)
			} else {
				// Zoom out (0 is set to ensure the zoom is reset to 1)
				instance.value.smoothZoomAbs(offsetX, offsetY, 0)
			}
		}

		return {
			guestNameStore,
			screen,
			screenClass,
			onDoubleClick,
			// Mirror fix
			showScreenPlaceholder,
			isLocalScreen,
			isRemoteScreen,
		}
	},

	computed: {
		model() {
			if (this.callParticipantModel) {
				return this.callParticipantModel
			}
			return this.localMediaModel
		},

		screenContainerId() {
			if (this.localMediaModel) {
				return 'localScreenContainer'
			}

			return 'container_' + this.callParticipantModel.attributes.peerId + '_screen_incoming'
		},

		remoteSessionHash() {
			if (!this.callParticipantModel) {
				return null
			}

			return Hex.stringify(SHA1(this.callParticipantModel.attributes.nextcloudSessionId))
		},

		remoteParticipantName() {
			if (!this.callParticipantModel) {
				return t('spreed', 'You')
			}

			let remoteParticipantName = this.callParticipantModel.attributes.name

			// The name is undefined and not shown until a connection is made
			// for registered users, so do not fall back to the guest name in
			// the store either until the connection was made.
			if (!this.callParticipantModel.attributes.userId && !remoteParticipantName && remoteParticipantName !== undefined) {
				remoteParticipantName = this.guestNameStore.getGuestName(
					this.token,
					this.remoteSessionHash,
				)
			}

			return remoteParticipantName
		},
	},

	watch: {

		'localMediaModel.attributes.localScreen': function(localScreen) {
			this._setScreen(localScreen)
		},

		'callParticipantModel.attributes.screen': function(screen) {
			this._setScreen(screen)
		},

	},

	mounted() {
		// Set initial state
		if (this.localMediaModel) {
			this._setScreen(this.localMediaModel.attributes.localScreen)
		} else {
			this._setScreen(this.callParticipantModel.attributes.screen)
		}
	},

	methods: {
		t,

		_setScreen(screen) {
			if (!screen) {
				this.$refs.screen.srcObject = null

				return
			}

			// The audio is played using an audio element in the model to be
			// able to hear it even if there is no view for it.
			attachMediaStream(screen, this.$refs.screen)

			// Modern browsers prevent streaming own tab and switching to it later.
			// Guard is against 'window', 'monitor', undefined (unsupported in Safari/Firefox)
			if (this.isLocalScreen) {
				if (screen.getVideoTracks?.()[0]?.getSettings?.().displaySurface === 'browser') {
					// Chromium only, if tab (not self) from current browser is shared - no mirroring
					this.showScreenPlaceholder = false
				} else {
					this.showScreenPlaceholder = true
				}
			}

			this.$refs.screen.muted = true
		},

		stopLocalScreen() {
			this.localMediaModel.stopSharingScreen()
		},
	},

}
</script>

<style lang="scss" scoped>
.screenContainer {
	width: 100%;
	height: 100%;
}

.screen {
	width: 100%;
	height: 100%;
	position: absolute;
	top: 0;
	inset-inline-start: 0;
	&--fit {
		object-fit: contain;
	}
	&--fill {
		object-fit: cover;
	}
	&--magnify {
		cursor: zoom-in;
	}
	&--grab {
		cursor: grab;
	}
	&--grabbing {
		cursor: grabbing;
	}
}

.screen-placeholder {
	width: 100%;
	height: 100%;
	position: absolute;
	inset: 0;
	color: var(--color-main-text);
	background-color: rgba(var(--color-main-background-rgb), 0.95);

	&__actions {
		display: flex;
		gap: var(--default-grid-baseline);
	}
}

</style>
